import classNames from 'classnames';
import { Fragment, h, JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';

import { LogoutForm, LogoutFormState } from './LogoutForm';

interface MenuItemData {
    active: boolean;
    children: MenuItemData[];
    href: string;
    icon_class: string;
    id: number;
    title: string;
    // There are more fields, but we don't use them
}

interface MenuHeaderState {
    logoUrl: string;
    dashboardUrl: string;
    userName: string;
    title: string;
}

interface MenuFooterState {
    logoUrl: string;
    dashboardUrl: string;
    userName: string;
    title: string;
}

type MenuHeaderProps = MenuHeaderState;

interface MenuFooterProps extends MenuFooterState {
    onClick: () => void;
}

interface UserMenuState {
    profile: {
        route: string;
        text: string;
    };
    logout: LogoutFormState;
}

export interface MenuState {
    header: MenuHeaderState;
    userMenu: UserMenuState;
    items: MenuItemData[];
    footer: MenuFooterState;
    // On desktop the sidebar collapses to a column of icons; some pages start collapsed (App\Support\CompactSidebar)
    startsCollapsed?: boolean;
    railLabels?: RailLabels;
}

interface MenuProps extends MenuState {
    className?: string;
    // Set by the desktop Menu; the mobile menu leaves them out
    railOpen?: boolean;
    openRail?: () => void;
}

interface RailLabels {
    expand: string;
    collapse: string;
}

interface MenuItemProps {
    item: MenuItemData;
    isExpanded?: boolean;
    onClick?: () => void;
    // The title shows as a tooltip while only the icon is visible
    hasTooltip?: boolean;
}

const MenuHeader = (props: MenuHeaderProps): JSX.Element => (
    <div class="k-menu__header">
        <img src={props.logoUrl} alt="Logo Icon" class="k-menu__header-icon" />
    </div>
);

const UserMenu = (props: UserMenuState): JSX.Element => (
    <div class="k-user-menu">
        <a class="k-user-menu__item" href={props.profile.route}>
            {props.profile.text}
        </a>
        <LogoutForm {...props.logout} buttonClass="k-user-menu__item" />
    </div>
);

const MenuItem = ({ item, isExpanded, onClick, hasTooltip }: MenuItemProps): JSX.Element => {
    const className = classNames('k-menu__item', item.active ? 'k-menu__item--active' : 'k-menu__item--inactive');

    const hasChildren = item.children.length > 0;
    const angleClass = isExpanded ? 'voyager-angle-up' : 'voyager-angle-down';

    return (
        <Fragment>
            <li class={className}>
                <a
                    href={hasChildren ? undefined : item.href}
                    onClick={onClick}
                    class="k-menu__link"
                    title={hasTooltip ? item.title : undefined}
                >
                    <div class="k-menu__link-text">
                        {item.icon_class === 'svg-dashboard' && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="k-menu__svg-logo"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                />
                            </svg>
                        )}
                        {item.icon_class === 'svg-ticket' && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="k-menu__svg-logo"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                                />
                            </svg>
                        )}
                        {item.icon_class === 'svg-calendar' && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="k-menu__svg-logo"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                        )}
                        {item.icon_class === 'svg-film' && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="k-menu__svg-logo"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                                />
                            </svg>
                        )}
                        {item.icon_class === 'svg-report' && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="k-menu__svg-logo"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                        )}
                        {item.icon_class === 'svg-terminal' && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="k-menu__svg-logo"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                        )}
                        {item.icon_class === 'svg-cog' && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="k-menu__svg-logo"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                            </svg>
                        )}
                        {item.icon_class === 'svg-qr' && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="k-menu__svg-logo"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                                />
                            </svg>
                        )}
                        <span>{item.title}</span>
                    </div>
                    {hasChildren && <span class={classNames('k-menu__angle icon', angleClass)}></span>}
                </a>
            </li>
            {hasChildren && isExpanded && (
                <ul class="k-menu__group">
                    {item.children.map((child) => (
                        <MenuItem item={child} />
                    ))}
                </ul>
            )}
        </Fragment>
    );
};

const MenuFooter = (props: MenuFooterProps): JSX.Element => (
    <div class="k-menu__footer">
        <div class="k-menu__profile" onClick={props.onClick}>
            <svg
                class="k-menu__profile-icon"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
        </div>
        <div class="k-menu__kinola">
            <svg class="k-menu__kinola-icon" width="51" height="13" viewBox="0 0 51 13" fill="none">
                <g opacity="0.2">
                    <path
                        d="M8.2684 10.4045H8.78626V12.7974H7.97495C6.36961 12.7974 4.76426 12.3901 3.93569 10.71L3.6595 10.2687C3.297 10.3196 2.91725 10.3706 2.52022 10.3875V12.7974H0V0.578125H2.52022V5.24523C3.46962 4.44758 4.64343 3.93844 5.86901 3.93844C7.62972 3.93844 8.78626 4.97369 8.78626 6.48413C8.78626 7.77395 7.94043 8.82617 6.43865 9.52199C6.85294 10.099 7.49162 10.4045 8.2684 10.4045ZM2.52022 8.36794C4.78152 8.11337 6.23151 7.53635 6.23151 6.77264C6.23151 6.43322 5.9208 6.19562 5.38568 6.19562C4.47081 6.19562 3.27974 6.84053 2.52022 7.70606V8.36794Z"
                        fill="#1F2126"
                    ></path>
                    <path
                        d="M9.53418 1.51044C9.53418 0.644909 10.2074 0 11.0877 0C11.9681 0 12.6586 0.644909 12.6586 1.51044C12.6586 2.37598 11.9854 3.03786 11.0877 3.03786C10.1901 3.03786 9.53418 2.39295 9.53418 1.51044ZM9.82763 4.14099H12.3479V12.7963H9.82763V4.14099Z"
                        fill="#1F2126"
                    ></path>
                    <path
                        d="M23.1737 10.4034V12.7964H22.4659C20.688 12.7964 19.7731 11.9308 19.7731 10.2507V8.06137C19.7731 7.00915 19.0999 6.3303 18.0814 6.3303C16.9939 6.3303 16.2517 7.02612 16.2517 8.09531V12.7964H13.7314V4.141H16.2517V5.09139C16.9249 4.32769 17.8398 3.93735 18.9272 3.93735C20.9469 3.93735 22.2933 5.24414 22.2933 7.22978V9.70758C22.2933 10.1488 22.5522 10.4034 23.001 10.4034H23.1737Z"
                        fill="#1F2126"
                    ></path>
                    <path
                        d="M23.1826 8.46868C23.1826 5.88904 25.2195 3.93735 27.9296 3.93735C30.6397 3.93735 32.6766 5.88904 32.6766 8.46868C32.6766 11.0483 30.6397 13 27.9296 13C25.2195 13 23.1826 11.0483 23.1826 8.46868ZM30.0873 8.46868C30.0873 7.24675 29.1725 6.3303 27.9296 6.3303C26.6868 6.3303 25.7719 7.24675 25.7719 8.46868C25.7719 9.69061 26.6868 10.6071 27.9296 10.6071C29.1725 10.6071 30.0873 9.69061 30.0873 8.46868Z"
                        fill="#1F2126"
                    ></path>
                    <path
                        d="M40.3411 10.405V12.798H39.6507C38.3215 12.798 37.1477 12.4076 36.1638 11.7288C35.4906 12.17 34.7656 12.5264 33.9715 12.798L33.0049 10.8123C33.54 10.6087 34.0579 10.3371 34.5239 10.0317C33.6436 8.72487 33.1602 7.02774 33.1602 5.10999C33.1602 2.0891 34.593 0.375 36.7507 0.375C38.9084 0.375 40.1858 1.91939 40.1858 4.54993C40.1858 6.5186 39.4435 8.45333 38.1316 9.98074C38.6322 10.2523 39.1846 10.405 39.7888 10.405H40.3411ZM35.7495 4.9233C35.7495 6.12826 35.9912 7.2314 36.44 8.13088C37.165 7.07866 37.5793 5.83975 37.5793 4.54993C37.5793 3.39589 37.2513 2.76795 36.6989 2.76795C36.1465 2.76795 35.7495 3.4468 35.7495 4.9233Z"
                        fill="#1F2126"
                    ></path>
                    <path
                        d="M50.9996 10.4034V12.7964H50.2919C49.049 12.7964 48.2205 12.3721 47.8407 11.5405C47.1675 12.44 46.1663 13 44.9407 13C42.5241 13 40.6943 11.0483 40.6943 8.46868C40.6943 5.88904 42.5241 3.93735 44.9407 3.93735C46.0282 3.93735 46.9431 4.3786 47.5991 5.09139V4.141H50.1193V9.70758C50.1193 10.1488 50.3782 10.4034 50.827 10.4034H50.9996ZM47.5991 8.46868C47.5991 7.24675 46.6842 6.3303 45.4413 6.3303C44.1985 6.3303 43.3009 7.24675 43.3009 8.46868C43.3009 9.69061 44.2157 10.6071 45.4413 10.6071C46.6669 10.6071 47.5991 9.69061 47.5991 8.46868Z"
                        fill="#1F2126"
                    ></path>
                </g>
            </svg>
        </div>
    </div>
);

export const MenuItems = (props: MenuProps): JSX.Element => {
    const [expandedId, setExpandedId] = useState(() => {
        for (const item of props.items) {
            if (item.active) {
                return item.id;
            }
        }

        return 0;
    });

    return (
        <ul class={classNames('k-menu__items', props.className)}>
            {props.items.map((item) => {
                const itemProps: MenuItemProps = { item, hasTooltip: !!props.openRail && !props.railOpen };

                if (item.children.length) {
                    itemProps.isExpanded = expandedId === item.id;

                    itemProps.onClick = (): void => {
                        // A heading's icon in the collapsed column opens the menu on that heading
                        if (props.openRail && !props.railOpen) {
                            props.openRail();
                            setExpandedId(item.id);

                            return;
                        }

                        setExpandedId(expandedId === item.id ? 0 : item.id);
                    };
                }

                return <MenuItem {...itemProps} />;
            })}
        </ul>
    );
};

// The « / » button of the desktop sidebar. Its state lives in the body class `sidebar-rail-collapsed`,
// shared with the Filament sidebar; voyager::master sets it before the page paints.
interface RailButtonProps {
    isOpen: boolean;
    labels?: RailLabels;
    onClick: () => void;
}

const RailButton = ({ isOpen, labels, onClick }: RailButtonProps): JSX.Element => (
    <button
        type="button"
        class="k-menu__rail-button"
        aria-label={isOpen ? labels?.collapse : labels?.expand}
        onClick={onClick}
    >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={isOpen ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'}
            />
        </svg>
    </button>
);

export const Menu = (props: MenuProps): JSX.Element => {
    const [isUserMenuOpen, setUserMenuOpen] = useState(false);
    const toggleUserMenu = (): void => setUserMenuOpen(!isUserMenuOpen);

    const [railOpen, setRailOpen] = useState(!props.startsCollapsed);

    useEffect(() => {
        document.body.classList.toggle('sidebar-rail-collapsed', !railOpen);

        // The calendar sizes itself on window resize
        const timeout = window.setTimeout(() => window.dispatchEvent(new Event('resize')), 250);

        return (): void => window.clearTimeout(timeout);
    }, [railOpen]);

    return (
        <div class="k-menu">
            <RailButton isOpen={railOpen} labels={props.railLabels} onClick={(): void => setRailOpen(!railOpen)} />
            <MenuHeader {...props.header} />
            <MenuItems {...props} railOpen={railOpen} openRail={(): void => setRailOpen(true)} />
            <div class="k-menu__bottom">
                <MenuFooter {...props.footer} onClick={toggleUserMenu} />
                {isUserMenuOpen && <UserMenu {...props.userMenu} />}
            </div>
        </div>
    );
};
